import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";
import { CARD_FIELD_DEFS } from "@/lib/orgUtils";

const LAYOUTS = [["classic", "Classic Tree"], ["horizontal", "Horizontal"], ["outline", "Outline"], ["radial", "Radial"], ["team_grid", "Team Grid"]];

export default function ChartDefaults() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!settings) return null;
  const cd = settings.chart_defaults;

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.put("/settings", patch);
      toast.success("Defaults saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setCd = (patch) => save({ chart_defaults: { ...cd, ...patch } });
  const bands = cd.color_config?.bands || [];

  const updateBand = (i, patch) => {
    const next = bands.map((b, j) => (j === i ? { ...b, ...patch } : b));
    setCd({ color_config: { ...cd.color_config, bands: next } });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
      <section>
        <h3 className="font-black text-lg">Tenure color bands</h3>
        <p className="text-sm text-[#676a82] font-medium mb-4">These bands drive the tenure coloring on every chart view.</p>
        <div className="space-y-2">
          {bands.map((b, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl bg-white border border-[#181B34]/10" data-testid={`band-row-${i}`}>
              <input type="color" value={b.color} onChange={(e) => updateBand(i, { color: e.target.value })}
                className="w-9 h-9 rounded-lg border-0 cursor-pointer" data-testid={`band-color-${i}`} />
              <input value={b.label} onChange={(e) => updateBand(i, { label: e.target.value })}
                className="flex-1 h-9 px-2.5 rounded-lg border border-[#181B34]/12 text-sm font-bold" data-testid={`band-label-${i}`} />
              <div className="flex items-center gap-1 text-xs font-semibold text-[#676a82]">
                &lt;
                <input type="number" step="0.5" value={b.max_years ?? ""} placeholder="∞"
                  onChange={(e) => updateBand(i, { max_years: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-16 h-9 px-2 rounded-lg border border-[#181B34]/12 text-sm font-bold" data-testid={`band-max-${i}`} />
                yrs
              </div>
              {bands.length > 2 && (
                <button onClick={() => setCd({ color_config: { ...cd.color_config, bands: bands.filter((_, j) => j !== i) } })}
                  className="w-8 h-8 rounded-lg hover:bg-[#E2445C]/10 text-[#676a82] hover:text-[#E2445C] flex items-center justify-center"><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>
        {bands.length < 8 && (
          <button data-testid="add-band-btn" onClick={() => setCd({ color_config: { ...cd.color_config, bands: [...bands.slice(0, -1), { label: "New band", max_years: 10, color: "#579BFC" }, bands[bands.length - 1]] } })}
            className="mt-3 flex items-center gap-1.5 text-sm font-bold text-[#6161FF]"><Plus size={14} /> Add band</button>
        )}
        <div className="mt-5 flex items-center justify-between p-3 rounded-xl bg-white border border-[#181B34]/10">
          <span className="text-sm font-bold">Tenure coloring on by default</span>
          <Switch data-testid="default-color-enabled" checked={!!cd.color_config?.enabled}
            onCheckedChange={(v) => setCd({ color_config: { ...cd.color_config, enabled: v } })} />
        </div>
        <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-white border border-[#181B34]/10">
          <span className="text-sm font-bold">Show legend</span>
          <Switch data-testid="default-legend" checked={settings.chart_defaults.show_legend !== false}
            onCheckedChange={(v) => setCd({ show_legend: v })} />
        </div>
      </section>

      <section>
        <h3 className="font-black text-lg">New-view defaults</h3>
        <p className="text-sm text-[#676a82] font-medium mb-4">New chart views inherit these settings.</p>
        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Default layout</label>
        <div className="mt-1.5 flex gap-1.5 flex-wrap">
          {LAYOUTS.map(([v, l]) => (
            <button key={v} data-testid={`default-layout-${v}`} onClick={() => setCd({ default_layout: v })}
              className={`px-3 h-9 rounded-full text-xs font-bold ${cd.default_layout === v ? "bg-[#6161FF] text-white" : "bg-white border border-[#181B34]/10 text-[#4b4e63]"}`}>{l}</button>
          ))}
        </div>
        <label className="mt-5 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Default card fields</label>
        <div className="mt-1.5 space-y-1.5">
          {CARD_FIELD_DEFS.slice(0, 6).map((f) => (
            <div key={f.key} className="flex items-center justify-between p-2.5 rounded-xl bg-white border border-[#181B34]/10">
              <span className="text-sm font-bold">{f.label}</span>
              <Switch data-testid={`default-field-${f.key}`} checked={cd.card_fields?.includes(f.key)}
                onCheckedChange={(v) => setCd({ card_fields: v ? [...cd.card_fields, f.key] : cd.card_fields.filter((x) => x !== f.key) })} />
            </div>
          ))}
        </div>

        <h3 className="font-black text-lg mt-8">Employee self-service</h3>
        <p className="text-sm text-[#676a82] font-medium mb-3">Which fields employees may edit themselves.</p>
        <div className="space-y-1.5">
          {[["photo", "Photo upload"], ["linkedin_url", "LinkedIn URL"], ["date_of_birth", "Date of Birth"], ["address", "Address"], ["highest_qualification", "Highest Qualification"]].map(([k, l]) => (
            <div key={k} className="flex items-center justify-between p-2.5 rounded-xl bg-white border border-[#181B34]/10">
              <span className="text-sm font-bold">{l}</span>
              <Switch data-testid={`selfservice-${k}`} checked={settings.self_service?.[k] !== false}
                onCheckedChange={(v) => save({ self_service: { ...settings.self_service, [k]: v } })} />
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 rounded-xl bg-[#F0F3FF]">
          <p className="text-xs font-bold text-[#4b4e63]">Privacy: DOB & Address visibility in profile panel</p>
          <div className="mt-2 flex gap-1.5">
            {[["self_admin", "Self + Admin only"], ["everyone", "Everyone"]].map(([v, l]) => (
              <button key={v} data-testid={`privacy-${v}`}
                onClick={() => save({ panel_privacy: { date_of_birth: v, address: v } })}
                className={`px-3 h-8 rounded-full text-xs font-bold ${settings.panel_privacy?.date_of_birth === v ? "bg-[#6161FF] text-white" : "bg-white text-[#4b4e63]"}`}>{l}</button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
